/**
 * The KnowIt adapter for `health`, `documentTasks` and `nudges` — three top-level namespaces
 * that sit beside `automation` rather than inside it, because none of them is about flows/runs.
 * Wire convention matches the rest of W2 (see `knowitAutomation.ts`'s header): bare objects, or
 * a bare object whose top-level key happens to be `data` — never the `{data, policies}`
 * envelope. `notification-deliveries.list` is the one exception: `{pagination, data}`.
 *
 * **F-1, the wave's single highest-risk defect**: `teams.update` REPLACES the whole
 * preferences JSON server-side. `health.saveSettings` reads `auth.info` first and posts the
 * merged object — never a bare patch. See `saveSettings` below and its golden test.
 */
import type { DeliveryQuery } from "~/api/client";
import type {
  BillingUsage,
  DeliveryPage,
  DocumentTask,
  DocumentTaskInput,
  DocumentTaskPatch,
  FlowHealthSettings,
  FlowHealthSettingsPatch,
  Nudge,
  NudgeTarget,
  QuickWhenResult,
} from "~/api/opsTypes";

export type Json = Record<string, unknown>;

function obj(value: unknown): Json {
  return value && typeof value === "object" ? (value as Json) : {};
}
function list(value: unknown): Json[] {
  return Array.isArray(value) ? value.map(obj) : [];
}
function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}
function strOrNull(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}
function num(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
function numOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
function bool(value: unknown): boolean {
  return value === true;
}

/** What this namespace needs from the transport it is mounted on. */
export interface OpsTransport {
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
  /** `auth.info`'s raw response — `saveSettings` needs the WHOLE preferences object, untyped,
   * so an unrelated key this adapter does not model still survives the round trip. */
  authInfoRaw(): Promise<Json>;
}

export function createKnowItOps(transport: OpsTransport) {
  const toDocumentTask = (raw: Json): DocumentTask => ({
    id: str(raw.id),
    documentId: str(raw.documentId),
    title: str(raw.title),
    details: strOrNull(raw.details),
    status: raw.status === "done" ? "done" : "open",
    assigneeId: strOrNull(raw.assigneeId),
    dueDate: strOrNull(raw.dueDate),
    sourceQuote: strOrNull(raw.sourceQuote),
    relatedDocumentIds: Array.isArray(raw.relatedDocumentIds)
      ? raw.relatedDocumentIds.map((entry) => str(entry))
      : [],
    createdById: strOrNull(raw.createdById),
    createdAt: str(raw.createdAt, new Date(0).toISOString()),
    updatedAt: str(raw.updatedAt, new Date(0).toISOString()),
    completedAt: strOrNull(raw.completedAt),
  });

  const toNudge = (raw: Json): Nudge => {
    const channels = obj(raw.channels);
    return {
      id: str(raw.id),
      targetType:
        raw.targetType === "part" || raw.targetType === "task" || raw.targetType === "decision"
          ? raw.targetType
          : "document",
      documentId: strOrNull(raw.documentId),
      partId: strOrNull(raw.partId),
      recordId: strOrNull(raw.recordId),
      whenUtc: str(raw.whenUtc, new Date(0).toISOString()),
      timezone: str(raw.timezone, "UTC"),
      recurrenceRule: strOrNull(raw.recurrenceRule),
      note: strOrNull(raw.note),
      channels: { inApp: bool(channels.inApp), email: bool(channels.email) },
      status:
        raw.status === "fired" ||
        raw.status === "snoozed" ||
        raw.status === "done" ||
        raw.status === "cancelled"
          ? raw.status
          : "scheduled",
      firedAtUtc: strOrNull(raw.firedAtUtc),
      createdAt: str(raw.createdAt, new Date(0).toISOString()),
    };
  };

  return {
    health: {
      usage: async (days = 30): Promise<BillingUsage> => {
        const raw = obj(await transport.post("billing.usage", { days }));
        const data = obj(raw.data);
        const ai = obj(data.ai);
        const storage = obj(data.storage);
        return {
          windowDays: num(data.windowDays, days),
          ai: { tokensIn: num(ai.tokensIn), tokensOut: num(ai.tokensOut), total: num(ai.total) },
          storage: {
            artifactBytes: num(storage.artifactBytes),
            attachmentBytes: num(storage.attachmentBytes),
          },
          daily: list(data.daily).map((day) => ({
            date: str(day.date),
            tokensIn: num(day.tokensIn),
            tokensOut: num(day.tokensOut),
          })),
          recentRuns: list(data.recentRuns).map((run) => ({
            runId: str(run.runId),
            name: strOrNull(run.name),
            status: strOrNull(run.status),
            tokensIn: num(run.tokensIn),
            tokensOut: num(run.tokensOut),
            source: strOrNull(run.source),
            occurredAt: str(run.occurredAt, new Date(0).toISOString()),
          })),
        };
      },

      settings: async (): Promise<FlowHealthSettings> => {
        const raw = await transport.authInfoRaw();
        const team = obj(raw.team);
        const preferences = obj(team.preferences);
        return {
          flowsNotifyOnFailure: preferences.flowsNotifyOnFailure === true,
          flowsBillingEnabled: preferences.flowsBillingEnabled === true,
          flowsAuditRetentionDays: numOrNull(preferences.flowsAuditRetentionDays),
        };
      },

      /**
       * F-1: `teams.update` REPLACES the whole preferences object. A naive
       * `{preferences: patch}` post would wipe every other team preference for the workspace —
       * so this reads the CURRENT raw preferences (untyped — an unrelated key this adapter does
       * not model still survives), merges the patch over them, and posts the merged whole.
       */
      saveSettings: async (patch: FlowHealthSettingsPatch): Promise<FlowHealthSettings> => {
        const info = await transport.authInfoRaw();
        const team = obj(info.team);
        const currentPreferences = obj(team.preferences);
        const merged: Json = { ...currentPreferences };
        if (patch.flowsNotifyOnFailure !== undefined) {
          merged.flowsNotifyOnFailure = patch.flowsNotifyOnFailure;
        }
        if (patch.flowsBillingEnabled !== undefined) {
          merged.flowsBillingEnabled = patch.flowsBillingEnabled;
        }
        if ("flowsAuditRetentionDays" in patch) {
          merged.flowsAuditRetentionDays = patch.flowsAuditRetentionDays ?? null;
        }
        await transport.post("teams.update", { preferences: merged });
        return {
          flowsNotifyOnFailure: merged.flowsNotifyOnFailure === true,
          flowsBillingEnabled: merged.flowsBillingEnabled === true,
          flowsAuditRetentionDays: numOrNull(merged.flowsAuditRetentionDays),
        };
      },

      /**
       * W4 §E-5 — the same read-modify-write as `saveSettings` above, opened up to any
       * preference key.
       *
       * It lives here, beside `saveSettings`, rather than in the W4 adapter, because there
       * must be exactly ONE place in this codebase that knows `teams.update` replaces the
       * whole document. Two implementations of a merge is how one of them ends up posting a
       * bare patch, and the failure is silent: the toast says "Saved" while every preference
       * nobody was looking at is gone.
       */
      updatePreferences: async (patch: Json): Promise<Json> => {
        const info = await transport.authInfoRaw();
        const team = obj(info.team);
        const merged: Json = { ...obj(team.preferences), ...patch };
        await transport.post("teams.update", { preferences: merged });
        return merged;
      },

      deliveries: async (query: DeliveryQuery = {}): Promise<DeliveryPage> => {
        const raw = obj(
          await transport.post("notification-deliveries.list", {
            offset: query.offset ?? 0,
            limit: query.limit ?? 25,
            ...(query.channel ? { channel: query.channel } : {}),
            ...(query.status ? { status: query.status } : {}),
          }),
        );
        const pagination = obj(raw.pagination);
        return {
          offset: num(pagination.offset),
          limit: num(pagination.limit, 25),
          total: num(pagination.total),
          data: list(raw.data).map((entry) => ({
            id: str(entry.id),
            notificationId: strOrNull(entry.notificationId),
            event: str(entry.event),
            userId: strOrNull(entry.userId),
            userName: strOrNull(entry.userName),
            userEmail: strOrNull(entry.userEmail),
            channel: str(entry.channel),
            status: str(entry.status),
            attemptedAt: str(entry.attemptedAt, new Date(0).toISOString()),
            error: strOrNull(entry.error),
            detail: strOrNull(entry.detail),
          })),
        };
      },
    },

    documentTasks: {
      list: async (
        query: { documentId?: string; assignedToMe?: boolean; includeDone?: boolean } = {},
      ): Promise<DocumentTask[]> => {
        const raw = obj(
          await transport.post("document-tasks.list", {
            ...(query.documentId ? { documentId: query.documentId } : {}),
            ...(query.assignedToMe ? { assignedToMe: true } : {}),
            ...(query.includeDone ? { includeDone: true } : {}),
          }),
        );
        return list(raw.data).map(toDocumentTask);
      },

      create: async (input: DocumentTaskInput): Promise<DocumentTask> => {
        const raw = obj(
          await transport.post("document-tasks.create", {
            documentId: input.documentId,
            title: input.title,
            ...(input.details ? { details: input.details } : {}),
            ...(input.assigneeId ? { assigneeId: input.assigneeId } : {}),
            ...(input.dueDate ? { dueDate: input.dueDate } : {}),
            ...(input.sourceQuote ? { sourceQuote: input.sourceQuote } : {}),
            ...(input.relatedDocumentIds ? { relatedDocumentIds: input.relatedDocumentIds } : {}),
          }),
        );
        return toDocumentTask(obj(raw.data));
      },

      /**
       * `hasAssignee`/`hasDueDate` are the tri-state discriminators the server needs to tell
       * "unchanged" from "cleared" — never omitted when the caller means to touch the field.
       */
      update: async (id: string, patch: DocumentTaskPatch): Promise<DocumentTask> => {
        const raw = obj(
          await transport.post("document-tasks.update", {
            id,
            ...(patch.title !== undefined ? { title: patch.title } : {}),
            ...(patch.details !== undefined ? { details: patch.details } : {}),
            ...(patch.hasAssignee !== undefined
              ? { hasAssignee: patch.hasAssignee, assigneeId: patch.assigneeId ?? null }
              : {}),
            ...(patch.hasDueDate !== undefined
              ? { hasDueDate: patch.hasDueDate, dueDate: patch.dueDate ?? null }
              : {}),
            ...(patch.relatedDocumentIds !== undefined
              ? { relatedDocumentIds: patch.relatedDocumentIds }
              : {}),
          }),
        );
        return toDocumentTask(obj(raw.data));
      },

      complete: async (id: string, done: boolean): Promise<DocumentTask> => {
        const raw = obj(await transport.post("document-tasks.complete", { id, done }));
        return toDocumentTask(obj(raw.data));
      },

      remove: async (id: string): Promise<void> => {
        await transport.post("document-tasks.delete", { id });
      },
    },

    nudges: {
      list: async (documentId?: string, includeResolved = false): Promise<Nudge[]> => {
        const raw = obj(
          await transport.post("nudges.list", {
            ...(documentId ? { documentId } : {}),
            ...(includeResolved ? { includeResolved: true } : {}),
          }),
        );
        return list(raw.data).map(toNudge);
      },

      /**
       * Email is opt-in, estate-wide (standing rule): `email` defaults to false and is sent
       * only when the caller explicitly asked for it.
       */
      create: async (target: NudgeTarget, when: QuickWhenResult, email = false): Promise<Nudge> => {
        const raw = obj(
          await transport.post("nudges.create", {
            targetType: target.targetType,
            ...(target.documentId ? { documentId: target.documentId } : {}),
            ...(target.partId ? { partId: target.partId } : {}),
            ...(target.recordId ? { recordId: target.recordId } : {}),
            whenUtc: when.whenUtc,
            timezone: when.timezone,
            ...(when.recurrenceRule ? { recurrenceRule: when.recurrenceRule } : {}),
            ...(when.note ? { note: when.note } : {}),
            channels: { inApp: true, email },
          }),
        );
        return toNudge(obj(raw.data));
      },

      snooze: async (id: string, whenUtc: string): Promise<Nudge> => {
        const raw = obj(await transport.post("nudges.update", { id, action: "snooze", whenUtc }));
        return toNudge(obj(raw.data));
      },

      markDone: async (id: string): Promise<Nudge> => {
        const raw = obj(await transport.post("nudges.update", { id, action: "done" }));
        return toNudge(obj(raw.data));
      },

      cancel: async (id: string): Promise<Nudge> => {
        const raw = obj(await transport.post("nudges.cancel", { id }));
        return toNudge(obj(raw.data));
      },
    },
  };
}
